<script setup>
import { computed, ref, watch } from 'vue'
import _cloneDeep from 'lodash-es/cloneDeep'
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    default: () => []
  },
  itemType: {
    type: String,
    default: 'Item'
  },
  optionLabel: {
    type: String,
    default: 'text'
  },
  optionValue: {
    type: String,
    default: 'value'
  },
  optionGroupLabel: {
    type: String
  },
  optionGroupChildren: {
    type: String
  },
  height: {
    type: Number,
    default: 450
  },
  width: {
    type: Number,
    default: 300
  }
})
const emit = defineEmits('update:modelValue')
const sortedOptions = ref([])
const selectPT = {
  root: {
    style: 'max-width: 100%'
  },
  list: {
    class: 'p-0'
  },
  listContainer: {
    style: 'max-height: none;'
  },
  emptyMessage: {
    class: 'p-0'
  }
}
const listboxPT = {
  root: {
    class: 'border-0'
  }
}
const selectedItems = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
  }
})
const display = computed(() => {
  if (selectedItems.value.length === 0) {
    return `Select ${props.itemType}(s)`
  }
  return `${props.itemType}s (${selectedItems.value.length})`
})
const sortSelectedToTop = () => {
  const optionClone = _cloneDeep(props.options)
  if (props.optionGroupLabel && props.optionGroupChildren) {
    const selected = { [props.optionGroupLabel]: 'Selected', [props.optionGroupChildren]: [] }
    for (const group of optionClone) {
      let c = 0
      const remove = []
      for (const option of group[props.optionGroupChildren]) {
        const value = typeof option === 'string' ? option : option[props.optionValue]
        if (selectedItems.value.includes(value)) {
          selected[props.optionGroupChildren].push(option)
          remove.push(c)
        }
        c += 1
      }
      group[props.optionGroupChildren] = group[props.optionGroupChildren].filter((_, i) => !remove.includes(i))
    }
    if (selected[props.optionGroupChildren].length > 0) {
      optionClone.unshift(selected)
    }
    sortedOptions.value = optionClone
  } else {
    sortedOptions.value = optionClone.sort((a, b) => {
      const avalue = typeof a === 'string' ? a : a[props.optionValue]
      const bvalue = typeof b === 'string' ? b : b[props.optionValue]
      const aSelected = selectedItems.value.includes(avalue)
      const bSelected = selectedItems.value.includes(bvalue)
      return aSelected && bSelected ? 0 : aSelected ? -1 : bSelected ? 1 : 0
    })
  }
}
watch(
  () => props.options,
  (value, old) => {
    if (old?.length !== value.length) {
      sortSelectedToTop()
    }
  },
  { immediate: true }
)
</script>

<template>
  <p-select
    :pt="selectPT"
    @hide="sortSelectedToTop()"
  >
    <template #value>{{ display }}</template>
    <template #empty>
      <p-listbox
        v-model="selectedItems"
        multiple
        filter
        :options="sortedOptions"
        :option-label="typeof options[0] === 'object' ? props.optionLabel : undefined"
        :option-value="typeof options[0] === 'object' ? props.optionValue : undefined"
        :option-group-label="props.optionGroupLabel"
        :option-group-children="props.optionGroupChildren"
        auto-option-focus
        checkmark
        :list-style="`height:${height}px; width: ${width};`"
        :virtual-scroller-options="{ itemSize: 38 }"
        :pt="listboxPT"
      >
        <template #optiongroup="slotProps">
          <div class="d-flex-center">
            <div class="pr-4 text-muted">{{ slotProps.option[props.optionGroupLabel] }}</div>
            <div class="flex-grow-1 border-bottom"></div>
          </div>
        </template>
      </p-listbox>
    </template>
  </p-select>
</template>
